import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeAt from '@angular/common/locales/de-AT';
import localeCh from '@angular/common/locales/de-CH';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import { inject, Injectable, InjectionToken, Injector, makeStateKey, REQUEST, signal, TransferState } from '@angular/core';
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { firstValueFrom, Observable } from 'rxjs';
import { AppLanguage, ISOLanguage } from '../../models/applanguage/app-language';
import { SystemService } from '../system.service';

/**
 * Default frontend-only language fallback.
 * Used for i18n display when no backend-confirmed language is available.
 * Never sent as API header or persisted to localStorage.
 */
const FRONTEND_DEFAULT_LANGUAGE: AppLanguage = 'de-DE';

/** Cookie persisting the selected language; readable during SSR so the `language` header is set server-side. */
export const LANGUAGE_COOKIE_NAME = 'gs.language';

/** Transfers the language the server rendered with to the hydrating browser. */
const PREFERRED_USER_LANGUAGE_KEY = makeStateKey<string>('preferredUserLanguage');

/** Transfers the language the server finished rendering with (after the page response) to the hydrating browser. */
const RENDERED_LANGUAGE_KEY = makeStateKey<AppLanguage>('renderedLanguage');

/**
 * Languages whose translation files hold every key, so they need no fallback language. Without a fallback the
 * page only loads (and the server only transfers) its own translations, not those of the default language too.
 */
export const COMPLETE_LANGUAGES: AppLanguage[] = ['de-DE', 'de-AT', 'de-CH'];

/**
 * The visitor's preferred language: the first entry of the `Accept-Language` header on the server, the
 * same value from the TransferState in the browser. The browser does not read `navigator.language` while a
 * server value exists: the header can differ from it (e.g. a language list set apart from the UI language),
 * and then the page request (`userLanguages`) missed the transfer cache, so the page was fetched and
 * rendered a second time. `navigator.language` only without a server render. An empty string without one.
 */
export const PREFERRED_USER_LANGUAGE = new InjectionToken<string>('PREFERRED_USER_LANGUAGE', {
  providedIn: 'root',
  factory: () => {
    const request = inject(REQUEST, { optional: true });
    const transferState = inject(TransferState);
    if (!request) return transferState.get(PREFERRED_USER_LANGUAGE_KEY, navigator.language);
    const language = request.headers.get('accept-language')?.split(',')[0]?.split(';')[0]?.trim() ?? '';
    transferState.set(PREFERRED_USER_LANGUAGE_KEY, language);
    return language;
  }
});

// Register the default locale eagerly so Angular pipes (DatePipe, CurrencyPipe) work
// before LanguageService.initialize() is called. Other locales are registered on demand.
registerLocaleData(localeDe);

/**
 * Manages the application language with a two-tier approach:
 *
 * **Display language** (frontend-only):
 * Used for i18n translations and the HTML lang attribute.
 * Resolved in order: localStorage > browser language > FRONTEND_DEFAULT_LANGUAGE.
 * Always has a value so the UI is never without translations.
 *
 * **API language** (`currentLang`):
 * Sent as the `language` HTTP header to the backend via the auth interceptor.
 * Only set when the backend has confirmed a language (via `setLanguageFromBackend`)
 * or the user explicitly switched (via `setLanguage`). Persisted in localStorage.
 * When `undefined`, no `language` header is sent — the backend determines the language.
 *
 * **Flow:**
 * 1. `initialize()` → loads `currentLang` from localStorage (if previously saved).
 *    Uses browser language or default as display language for i18n.
 *    API requests are sent **without** `language` header if no localStorage entry exists.
 * 2. Backend responds with a language on the `/cms` endpoint.
 * 3. `setLanguageFromBackend()` → sets `currentLang`, persists to localStorage,
 *    updates i18n and HTML lang attribute.
 * 4. All subsequent API requests include the `language` header from `currentLang`.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly locales: Record<AppLanguage, any> = {
    'de-DE': localeDe,
    'en-US': localeEn,
    'nl-NL': localeNl,
    'de-CH': localeCh,
    'de-AT': localeAt,
    'fr-FR': localeFr
  };

  private readonly langAttributeMap: Record<AppLanguage, ISOLanguage> = {
    'de-DE': 'de',
    'en-US': 'en',
    'nl-NL': 'nl',
    'de-CH': 'de-CH',
    'de-AT': 'de-AT',
    'fr-FR': 'fr'
  };

  /**
   * The backend-confirmed language. Persisted in localStorage.
   * Sent as `language` HTTP header — `undefined` means no header is sent.
   */
  public currentLang?: AppLanguage;

  /** All languages supported by the application. */
  public supportedLangs: AppLanguage[] = ['de-DE', 'en-US', 'nl-NL', 'de-CH', 'de-AT', 'fr-FR'];

  /** localStorage key used to persist the backend-confirmed language across sessions. */
  private readonly storageKey = 'app-language';

  /** BCP 47 lang attribute value for the HTML element. Always has a value. */
  public readonly langAttribute = signal<ISOLanguage>('de');

  /** The active AppLanguage for Angular's LOCALE_ID. Always has a value. */
  public readonly locale = signal<AppLanguage>(FRONTEND_DEFAULT_LANGUAGE);

  private readonly injector = inject(Injector);
  private readonly systemService = inject(SystemService);
  private readonly cookieService = inject(SsrCookieService);
  /** The visitor's preferred language, the same on the server and in the hydrating browser */
  private readonly preferredUserLanguage = inject(PREFERRED_USER_LANGUAGE);
  private readonly transferState = inject(TransferState);

  /**
   * Lazily resolved to break the circular dependency:
   * LanguageService → TranslateService → TranslateHttpLoader → HttpClient → authInterceptor → LanguageService
   */
  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  /**
   * Initializes the language service.
   * Loads `currentLang` from localStorage if available (backend-confirmed from a previous session).
   * Sets the display language for i18n: `currentLang` > browser language > default.
   * Does NOT set `currentLang` from browser language — only localStorage or backend can do that.
   */
  public async initialize(): Promise<void> {
    this.loadLanguageFromStorage();

    this.translate.addLangs(this.supportedLangs);

    // The browser starts with the language the server ended with, so it does not load the browser language first
    // and then switch, and the server only transfers the translations of that one language.
    let displayLang = this.currentLang ?? this.transferState.get(RENDERED_LANGUAGE_KEY, null) ?? this.getDisplayLanguageFallback();
    if (!this.systemService.browser) this.transferState.onSerialize(RENDERED_LANGUAGE_KEY, () => this.locale());

    try {
      await firstValueFrom(this.useTranslations(displayLang));
    } catch (error) {
      console.error(`Failed to load language ${displayLang}, falling back to ${FRONTEND_DEFAULT_LANGUAGE}`, error);
      displayLang = FRONTEND_DEFAULT_LANGUAGE;
      await firstValueFrom(this.useTranslations(displayLang));
    }

    this.langAttribute.set(this.langAttributeMap[displayLang]);
    this.locale.set(displayLang);
    this.registerLocale(displayLang);
  }

  /**
   * Explicitly switches the language (e.g. from a language picker).
   * Sets `currentLang`, persists to localStorage, and updates i18n + HTML lang attribute.
   */
  setLanguage(lang: AppLanguage): void {
    if (!this.supportedLangs.includes(lang)) {
      console.warn(`Language ${lang} is not supported.`);
      return;
    }
    this.currentLang = lang;
    this.useTranslations(lang);
    this.saveLanguageToStorage(lang);
    this.langAttribute.set(this.langAttributeMap[lang]);
    this.locale.set(lang);
    this.registerLocale(lang);
  }

  /**
   * Returns the backend-confirmed language, or `undefined` if the backend hasn't responded yet.
   */
  getCurrentLanguage(): string | undefined {
    return this.currentLang;
  }

  /**
   * Called when the backend provides a language (e.g. from a /cms page response).
   * Sets `currentLang`, persists to localStorage, and updates i18n + HTML lang attribute.
   * After this call, all subsequent API requests will include the `language` header.
   * @param isoCode BCP 47 language code from the backend (e.g., 'de', 'en', 'nl', 'de-CH')
   */
  setLanguageFromBackend(isoCode: ISOLanguage): void {
    if (!isoCode) return;

    this.langAttribute.set(isoCode);

    const matchingLang = this.findMatchingAppLanguage(isoCode);
    if (matchingLang) {
      if (matchingLang !== this.currentLang) {
        this.currentLang = matchingLang;
        this.useTranslations(matchingLang);
        this.locale.set(matchingLang);
        this.registerLocale(matchingLang);
      }
      this.saveLanguageToStorage(matchingLang);
    }
  }

  /**
   * Loads `currentLang` from a previously persisted language, if valid.
   * Prefers the language cookie (readable during SSR) and falls back to localStorage in the browser.
   * Only called during initialization. Does not fall back to browser language.
   */
  private loadLanguageFromStorage(): void {
    const cookieLang = this.cookieService.get(LANGUAGE_COOKIE_NAME);
    if (cookieLang && this.supportedLangs.includes(cookieLang as AppLanguage)) {
      this.currentLang = cookieLang as AppLanguage;
      return;
    }
    if (this.systemService.browser) {
      const savedLang = localStorage.getItem(this.storageKey);
      if (savedLang && this.supportedLangs.includes(savedLang as AppLanguage)) {
        this.currentLang = savedLang as AppLanguage;
      }
    }
  }

  /** Persists the given language to localStorage for use in future sessions. */
  private saveLanguageToStorage(lang: string): void {
    this.cookieService.set(LANGUAGE_COOKIE_NAME, lang, { path: '/', expires: 365, secure: true, sameSite: 'Lax' });
    if (this.systemService.browser) {
      localStorage.setItem(this.storageKey, lang);
    }
  }

  /**
   * Returns the best display language when no backend-confirmed language is available.
   * Used only for frontend i18n — never persisted or sent as API header.
   * Priority: preferred user language (if supported) > FRONTEND_DEFAULT_LANGUAGE. The server and the
   * hydrating browser resolve the same language, see `PREFERRED_USER_LANGUAGE`.
   * @returns The display language.
   */
  private getDisplayLanguageFallback(): AppLanguage {
    const userLang = this.preferredUserLanguage as AppLanguage;
    return this.supportedLangs.includes(userLang) ? userLang : FRONTEND_DEFAULT_LANGUAGE;
  }

  /**
   * Switches the translations to the given language. Only incomplete languages fall back to the default language.
   * @param lang The language to translate into.
   * @returns An observable emitting the translations of the language.
   */
  private useTranslations(lang: AppLanguage): Observable<InterpolatableTranslationObject> {
    this.translate.setFallbackLang(COMPLETE_LANGUAGES.includes(lang) ? lang : FRONTEND_DEFAULT_LANGUAGE);
    return this.translate.use(lang);
  }

  /** Registers Angular locale data for the given language (used by pipes like DatePipe, CurrencyPipe). */
  private registerLocale(lang: AppLanguage): void {
    const localeData = this.locales[lang];
    if (localeData) {
      registerLocaleData(localeData);
    }
  }

  /**
   * Maps a BCP 47 ISO code from the backend to an internal AppLanguage.
   * Tries: direct match > langAttributeMap lookup > prefix match.
   */
  private findMatchingAppLanguage(isoCode: ISOLanguage): AppLanguage | undefined {
    if (this.supportedLangs.includes(isoCode as AppLanguage)) {
      return isoCode as AppLanguage;
    }

    const entry = Object.entries(this.langAttributeMap).find(([, value]) => value === isoCode);
    if (entry) {
      return entry[0] as AppLanguage;
    }

    return this.supportedLangs.find((lang) => lang.toLowerCase().startsWith(isoCode.toLowerCase()));
  }
}
